import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { trackView } from "@/lib/library-engagement";
import { ogImageMeta, ogImage as ogImage_, hubOgKey } from "@/lib/og-images";

import { getLibraryItemBySlug, getRelatedGems } from "@/lib/library.functions";
import { LibraryCard } from "@/components/library-card";
import { SiteFooter } from "@/components/site-footer";
import { GemDetailCard } from "@/components/gem-detail-card";
import { LibraryHelpCta } from "@/components/library-help-cta";
import { WebMcpGemTools, WebMcpLibraryTools } from "@/components/webmcp-provider";
import { hubSlug } from "@/lib/library-hubs";
import { gemTitle, gemDescription, isFreeLicense } from "@/lib/library-seo";
import { bestListsForGem } from "@/lib/library-best";

type LibraryItemRow = {
  id: string;
  slug: string;
  category: string;
  subcategory: string;
  name: string;
  description: string | null;
  url: string;
  domain: string;
  author: string | null;
  source_type: string | null;
  license: string | null;
  tags: string[];
  why_gem: string | null;
  og_image_url: string | null;
  og_card_url: string | null;
  og_full_url: string | null;
  og_status: string;
};

type RelatedGem = {
  slug: string;
  name: string;
  description: string | null;
  category: string;
  subcategory: string;
  og_image_url: string | null;
  og_card_url: string | null;
  og_status: string;
  license: string | null;
};

export const Route = createFileRoute("/library/$slug")({
  // Full SSR: the gem name, description and links must be in the raw HTML so
  // search engines and agents can read the page without executing JavaScript.
  loader: async ({ params }) => {
    try {
      const item = (await getLibraryItemBySlug({
        data: { slug: params.slug },
      })) as LibraryItemRow | null;
      if (!item) return { item: null, related: [] as RelatedGem[] };
      let related: RelatedGem[] = [];
      try {
        related = (await getRelatedGems({
          data: { subcategory: item.subcategory, excludeSlug: item.slug },
        })) as RelatedGem[];
      } catch {
        related = [];
      }
      return { item, related };
    } catch {
      return { item: null, related: [] as RelatedGem[] };
    }
  },
  head: ({ params, loaderData }) => {
    const item = loaderData?.item ?? null;
    const url = `https://vibing.inc/library/${item?.slug ?? params.slug}`;
    if (!item) {
      return {
        meta: [
          { title: "Gem not found — Hidden Gems Library" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    // Name first, then what it actually is: people search the tool's own name,
    // and the old taxonomy tail was eating the visible part of the title.
    const title = gemTitle(item);
    // Lead with the curated "why it's a gem" line: it is the most compelling
    // reason to click. Fall back to the plain description.
    const desc = gemDescription(item);

    const rawOg = item.og_full_url || item.og_image_url || "";
    // Crawlers ignore SVG previews, so our generated SVG fallback cards don't
    // qualify — those gems get the branded collage card for their shelf.
    const usableOg = rawOg && !rawOg.toLowerCase().endsWith(".svg") ? rawOg : "";
    const ogImage = usableOg
      ? usableOg.startsWith("/")
        ? `https://vibing.inc${usableOg}`
        : usableOg
      : ogImage_(hubOgKey(hubSlug(item.category)));
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImageMeta(ogImage, `Preview image for ${item.name}`),
      ],

      links: [
        { rel: "canonical", href: url },
        // Agent discovery from a deep link, not just from /library.
        { rel: "alternate", type: "text/markdown", href: `https://vibing.inc/md/library/${item.slug}`, title: "Markdown" },
        { rel: "alternate", type: "application/json", href: `https://vibing.inc/api/public/library/gems/${item.slug}`, title: "JSON" },
        { rel: "alternate", type: "text/plain", href: "https://vibing.inc/llms.txt", title: "llms.txt" },
        { rel: "service-desc", type: "application/json", href: "https://vibing.inc/api/public/openapi.json" },
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: item.name,
            description: desc,
            url,
            sameAs: item.url,
            genre: item.category,
            keywords: (item.tags ?? []).join(", "),
            ...(item.author ? { creator: { "@type": "Person", name: item.author } } : {}),
            ...(item.license ? { license: item.license } : {}),
            ...(ogImage ? { image: ogImage } : {}),
            isPartOf: {
              "@type": "Collection",
              name: "The Vibeary — Hidden Gems Library",
              url: "https://vibing.inc/library",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://vibing.inc/" },
              { "@type": "ListItem", position: 2, name: "The Vibeary", item: "https://vibing.inc/library" },
              {
                "@type": "ListItem",
                position: 3,
                name: item.category,
                item: `https://vibing.inc/library/c/${hubSlug(item.category)}`,
              },
              { "@type": "ListItem", position: 4, name: item.name, item: url },
            ],
          }),
        },
        // Most gems are software. Describing them as such (rather than only as
        // a generic creative work) makes the page eligible for richer results.
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: item.name,
            description: desc,
            url: item.url,
            applicationCategory: "DeveloperApplication",
            operatingSystem: "Web",
            ...(item.author ? { author: { "@type": "Person", name: item.author } } : {}),
            ...(item.license ? { license: item.license } : {}),
            ...(ogImage ? { image: ogImage } : {}),
            ...(isFreeLicense(item.license)
              ? { offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }
              : {}),
          }),
        },
      ],
    };
  },

  component: LibraryDetail,
});

function LibraryDetail() {
  const { item, related } = Route.useLoaderData();

  if (!item) {
    return (
      <div className="min-h-dvh bg-background text-foreground">
        <main id="main" className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl">Gem not found</h1>
          <Link to="/library" className="mt-4 inline-block text-muted-foreground underline">
            Back to library
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return <DetailView item={item} related={related ?? []} />;
}

function DetailView({ item, related }: { item: LibraryItemRow; related: RelatedGem[] }) {
  // Anonymous, deduped view ping (once per browser per gem per 12 hours).
  useEffect(() => {
    trackView(item.slug);
  }, [item.slug]);

  // Internal links from gem pages (which already rank) to the topic round-ups.
  const roundUps = bestListsForGem(item);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <WebMcpLibraryTools />
      <WebMcpGemTools slug={item.slug} name={item.name} url={item.url} />
      <main id="main" className="mx-auto max-w-4xl px-5 py-10 md:px-8 md:py-16">
        <Link
          to="/library"
          className="inline-flex min-h-[40px] items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <span aria-hidden>←</span> Back to library
        </Link>

        <div className="mt-6">
          <GemDetailCard item={item} />
        </div>

        <nav
          aria-label="Collections this gem belongs to"
          className="mt-8 flex flex-wrap items-center gap-2 text-sm"
        >
          <span className="text-muted-foreground">Browse more:</span>
          <a
            href={`/library/c/${hubSlug(item.category)}`}
            className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 transition-colors hover:bg-muted"
          >
            {item.category}
          </a>
          {item.subcategory ? (
            <a
              href={`/library/c/${hubSlug(item.category)}/${hubSlug(item.subcategory)}`}
              className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 transition-colors hover:bg-muted"
            >
              {item.subcategory}
            </a>
          ) : null}
          {(item.tags ?? []).slice(0, 5).map((t) => (
            <a
              key={t}
              href={`/library/t/${hubSlug(t)}`}
              className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              #{t}
            </a>
          ))}
        </nav>

        {roundUps.length > 0 ? (
          <nav
            aria-label="Round-ups featuring this gem"
            className="mt-4 flex flex-wrap items-center gap-2 text-sm"
          >
            <span className="text-muted-foreground">Featured in:</span>
            {roundUps.map((list) => (
              <a
                key={list.slug}
                href={`/library/best/${list.slug}`}
                className="inline-flex min-h-[36px] items-center rounded-full border border-border px-3 transition-colors hover:bg-muted"
              >
                {list.title}
              </a>
            ))}
          </nav>
        ) : null}

        {related.length > 0 ? (
          <section className="mt-16">
            <h2 className="font-display text-2xl tracking-tight">
              More from {item.subcategory}
            </h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <LibraryCard
                  key={r.slug}
                  slug={r.slug}
                  name={r.name}
                  description={r.description ?? ""}
                  category={r.category}
                  subcategory={r.subcategory}
                  tags={[]}
                  author=""
                  license={r.license ?? ""}
                  ogImageUrl={r.og_card_url ?? r.og_image_url ?? ""}
                />
              ))}
            </div>
          </section>
        ) : null}

        <LibraryHelpCta
          line={`We're full-stack designers and a vibe coding agency. If ${item.name} is part of something you're building — or something you vibe coded and can't finish — we design it, build it and ship it with you.`}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
